import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { SPACE_DEFAULT_TITLE } from '@gitbook/app-database';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import { getSuspense, mapSuspenseArray } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { SpaceIcon } from '..';
import { useNavigation } from '../../navigation';
import { apiResource, readAPICache } from '../../state';
import { SearchModalResults, type SearchResultInfo } from '../Search';

type SourceItem = SourceSpaceItem | SourcePageItem | SourceRecordItem;

interface SourceSpaceItem {
    type: 'space';
    space: api.Space;
}

interface SourcePageItem {
    type: 'page';
    space: api.Space;
    page: hiveCore.RevisionPage;
    ancestors: hiveCore.RevisionPage[];
    path: string;
}

interface SourceRecordItem {
    type: 'record';
    recordId: string;
    siteId: string;
    title: string;
    url: string;
}

/**
 * Render the sources of an answer.
 */
export function AISearchAnswerSources(props: { answer: api.SearchAIAnswer }): React.ReactElement {
    const { answer } = props;

    const { navigate } = useNavigation();
    const hiveClient = useHiveClient();
    const sources = React.useMemo(() => mapAnswerSources(hiveClient, answer), [hiveClient, answer]);

    const onOpen = React.useCallback(
        (source: SourceItem) => {
            analytics.logEvent({
                type: 'ai_search_open_source',
            });

            switch (source.type) {
                case 'space':
                    navigate({
                        screen: {
                            type: 'space',
                            space: source.space.id,
                            organization: source.space.organization,
                            page: '',
                            query: undefined,
                        },
                    });
                    break;
                case 'page':
                    navigate({
                        screen: {
                            type: 'space',
                            space: source.space.id,
                            organization: source.space.organization,
                            page: source.path,
                            query: undefined,
                        },
                    });
                    break;
                case 'record':
                    window.open(source.url, '_blank');
                    break;
                default:
                    assertNever(source);
            }
        },
        [navigate]
    );

    const renderItem = React.useCallback((source: SourceItem): SearchResultInfo => {
        switch (source.type) {
            case 'space':
                return {
                    icon: <SpaceIcon space={source.space} />,
                    title: source.space.title || SPACE_DEFAULT_TITLE,
                    action: 'Go to space',
                };
            case 'page':
                return {
                    icon: Icon.Page,
                    title: source.page.title,
                    action: 'Go to page',
                };
            case 'record':
                return {
                    icon: Icon.FileText,
                    title: source.title,
                    action: 'Open source URL',
                };
            default:
                assertNever(source);
        }
    }, []);

    return (
        <SearchModalResults
            data={sources}
            getKey={getSourceKey}
            renderItem={renderItem}
            onOpen={onOpen}
        />
    );
}

function mapAnswerSources(hiveClient: HiveClient, answer: api.SearchAIAnswer): SourceItem[] {
    const perSpaces = new Map<string, SourcePageItem[]>();
    const records = new Set<string>();
    const sourceItems: Array<SourceItem> = [];

    mapSuspenseArray(answer.sources, (source) => {
        if (source.type === 'record') {
            if (records.has(source.record)) {
                return null;
            }

            records.add(source.record);
            sourceItems.push({
                type: 'record',
                recordId: source.record,
                siteId: source.site,
                title: source.title,
                url: source.url,
            });

            return null;
        }

        const result = resolveSpacePageRevision(source.space, source.page, hiveClient);
        if (!result) {
            return null;
        }
        const ancestors = hiveCore.getPageAncestorsInRevision(result.revision, result.page.id);

        const pageSourceItem: SourcePageItem = {
            type: 'page',
            space: result.space,
            page: result.page,
            ancestors,
            path: hiveCore.getAbsolutePagePathInRevision(result.revision, result.page.id),
        };

        // Otherwise, push a SourceSpaceItem into the list of sources and keep track of which pages
        // appear in that space. We'll regroup them at the end.
        const spaceAlreadyInSources = sourceItems.some(
            (sourceItem) => sourceItem.type === 'space' && sourceItem.space.id === result.space.id
        );

        if (!spaceAlreadyInSources) {
            sourceItems.push({
                type: 'space',
                space: result.space,
            });
        }

        perSpaces.set(
            result.space.id,
            (perSpaces.get(result.space.id) || []).concat(pageSourceItem)
        );
    });

    // Flatten the list of results so that space items are expanded to
    // include their pages.
    const result: SourceItem[] = [];

    for (const sourceItem of sourceItems) {
        if (!sourceItem) {
            continue;
        }

        result.push(sourceItem);

        if (sourceItem.type === 'space') {
            // Push all the pages from the space
            const pages = perSpaces.get(sourceItem.space.id) || [];

            pages.forEach((page) => {
                result.push(page);
            });
        }
    }

    return result;
}

function resolveSpacePageRevision(
    spaceId: string,
    pageId: string,
    hiveClient: HiveClient
):
    | { space: api.Space; page: hiveCore.RevisionPageDocument; revision: hiveCore.Revision }
    | undefined {
    const space = readAPICache(apiResource('getSpaceById', { spaceId }, { nullOnError: true }));
    if (!space) {
        return undefined;
    }

    const revision = getSuspense(
        hiveClient.store.getRevision(spaceId, space.revision, { metadata: false })
    );
    if (!revision) {
        return undefined;
    }

    const page = revision.pages[pageId];
    if (!page || page.type !== hiveCore.RevisionPageType.Document) {
        return undefined;
    }

    return { space, page, revision };
}

function getSourceKey(source: SourceItem): string {
    switch (source.type) {
        case 'space':
            return `space:${source.space.id}`;
        case 'page':
            return `page:${source.space.id}/${source.page.id}`;
        case 'record':
            return `record:${source.siteId}/${source.recordId}`;
        default:
            assertNever(source);
    }
}
